/**
 * 平台简介管理模块 - 新版本（支持动态统计数据）
 */

import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Row, Col, Space, Divider, Typography, Switch, InputNumber, Spin, Modal } from 'antd';
import { SaveOutlined, ReloadOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import { platformService, PlatformInfo } from '../../../../../services/platformService';
import { handleApiError, handleApiResponse } from '../../../../../utils/errorHandler';
import StatisticsConfigComponent from './components/StatisticsConfigComponent';
import { useDict } from '../../../../../hooks/useDict';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PlatformManagementNew: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [statisticsConfigVisible, setStatisticsConfigVisible] = useState(false);
  const [statisticsMap, setStatisticsMap] = useState<Record<string, any>>({});
  const [currentStatisticsValues, setCurrentStatisticsValues] = useState<Record<string, any>>({});

  // 使用数据字典
  const { dictData: statisticsItemDict } = useDict('statistics_item');
  const { dictData: categoryDict } = useDict('statistics_category');

  // 加载平台简介信息
  const loadPlatformInfo = async () => {
    setLoading(true);
    try {
      const response = await platformService.getInfo();
      if (response.code === 200 && response.data) {
        setPlatformInfo(response.data);
        form.setFieldsValue(response.data);
        
        // 处理统计数据
        if (response.data.statisticsMap) {
          setStatisticsMap(response.data.statisticsMap);
          // 同时更新currentStatisticsValues，确保统计数据值被正确初始化
          setCurrentStatisticsValues(response.data.statisticsMap);
        } else if (response.data.statistics) {
          try {
            const parsedStats = JSON.parse(response.data.statistics);
            setStatisticsMap(parsedStats);
            // 同时更新currentStatisticsValues
            setCurrentStatisticsValues(parsedStats);
          } catch (e) {
            console.error('解析统计数据失败:', e);
            setStatisticsMap({});
            setCurrentStatisticsValues({});
          }
        } else {
          // 如果statistics字段为空，初始化为空对象
          setStatisticsMap({});
          setCurrentStatisticsValues({});
        }
      } else {
        handleApiError(response, '加载平台简介信息失败');
      }
    } catch (error: any) {
      handleApiError(error, '加载平台简介信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存平台简介信息
  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      // 根据当前的统计数据配置表构建statistics字段
      // 1. 获取当前所有的统计数据配置
      let statisticsValuesToSave: Record<string, any> = {};
      
      try {
        const configResponse = await platformService.getStatisticsConfig();
        if (configResponse.code === 200 && configResponse.data) {
          const configs = configResponse.data;
          
          // 构建配置键集合，用于快速查找
          const configKeysSet = new Set<string>();
          configs.forEach((config: any) => {
            const combinedKey = `${config.category}_${config.displayName}`;
            configKeysSet.add(combinedKey);
          });
          
          // 2. 合并当前输入的统计数值和已有的统计数值
          const allStatisticsValues = Object.keys(currentStatisticsValues).length > 0 
            ? currentStatisticsValues 
            : statisticsMap;
          
          // 3. 根据配置表构建statistics对象
          // 严格只包含配置表中存在的配置项的值，过滤掉所有不在配置表中的旧数据
          configs.forEach((config: any) => {
            const combinedKey = `${config.category}_${config.displayName}`;
            // 只包含配置表中存在的项，且值不为空
            if (configKeysSet.has(combinedKey) && 
                allStatisticsValues[combinedKey] !== undefined && 
                allStatisticsValues[combinedKey] !== null && 
                allStatisticsValues[combinedKey] !== '') {
              statisticsValuesToSave[combinedKey] = allStatisticsValues[combinedKey];
            }
          });
          
          // 4. 额外检查：确保没有遗漏的配置项（防止配置表中有重复或格式不一致的情况）
        } else {
          // 如果获取配置失败，使用现有的统计数据值
          statisticsValuesToSave = Object.keys(currentStatisticsValues).length > 0 
            ? currentStatisticsValues 
            : statisticsMap;
        }
      } catch (error) {
        console.error('获取统计数据配置失败，使用现有值:', error);
        // 如果获取配置失败，使用现有的统计数据值
        statisticsValuesToSave = Object.keys(currentStatisticsValues).length > 0 
          ? currentStatisticsValues 
          : statisticsMap;
      }
      
      const data: PlatformInfo = {
        ...values,
        id: platformInfo?.id,
        statisticsMap: statisticsValuesToSave, // 使用根据配置表构建的统计数值
        statistics: JSON.stringify(statisticsValuesToSave), // 同时保存JSON字符串格式
      };

      let response;
      if (platformInfo?.id) {
        response = await platformService.update(data);
      } else {
        response = await platformService.create(data);
      }

      if (handleApiResponse(response, platformInfo?.id ? '平台简介更新成功' : '平台简介创建成功')) {
        loadPlatformInfo(); // 重新加载数据
      }
    } catch (error: any) {
      handleApiError(error, platformInfo?.id ? '平台简介更新失败' : '平台简介创建失败');
    } finally {
      setLoading(false);
    }
  };

  // 刷新数据
  const handleRefresh = () => {
    loadPlatformInfo();
  };

  // 预览
  const handlePreview = () => {
    setPreviewVisible(true);
  };

  // 处理统计数据变化
  const handleStatisticsChange = (newStatisticsMap: Record<string, any>) => {
    setStatisticsMap(newStatisticsMap);
  };

  // 处理统计数值变化
  const handleStatisticsValuesChange = (statisticsValues: Record<string, any>) => {
    setCurrentStatisticsValues(statisticsValues);
  };

  useEffect(() => {
    loadPlatformInfo();
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Spin spinning={loading}>
        <Card title="平台简介管理" bordered={false}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              title: '平台简介',
              subtitle: '黄石经济技术开发区·铁山区知识产权服务业集聚区',
              content: '',
              achievements: '',
            }}
          >
            {/* 基本信息区域 */}
            <div style={{ marginBottom: 32 }}>
              <Title level={4} style={{ marginBottom: 16 }}>基本信息</Title>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="平台标题"
                    name="title"
                    rules={[{ required: true, message: '请输入平台标题' }]}
                  >
                    <Input placeholder="请输入平台标题" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="平台副标题"
                    name="subtitle"
                  >
                    <Input placeholder="请输入平台副标题" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="平台简介内容"
                name="content"
                rules={[{ required: true, message: '请输入平台简介内容' }]}
              >
                <TextArea rows={6} placeholder="请输入平台简介内容" />
              </Form.Item>

              <Form.Item
                label="主要成就"
                name="achievements"
              >
                <TextArea rows={4} placeholder="请输入主要成就描述" />
              </Form.Item>
            </div>

            {/* 操作按钮 */}
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Space size="large">
                <Button 
                  icon={<SettingOutlined />} 
                  onClick={() => setStatisticsConfigVisible(true)} 
                  size="large"
                >
                  统计数据配置
                </Button>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} size="large">
                  保存
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="large">
                  刷新
                </Button>
                <Button icon={<EyeOutlined />} onClick={handlePreview} size="large">
                  预览
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>

      {/* 预览模态框 */}
      <Modal
        title="平台简介预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {platformInfo && (
          <div>
            <Title level={2} style={{ textAlign: 'center', marginBottom: 16 }}>
              {platformInfo.title}
            </Title>
            {platformInfo.subtitle && (
              <Title level={4} style={{ textAlign: 'center', color: '#666', marginBottom: 24 }}>
                {platformInfo.subtitle}
              </Title>
            )}
            
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>平台简介</Title>
              <div style={{ 
                lineHeight: '1.8', 
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '6px'
              }}>
                {platformInfo.content}
              </div>
            </div>

            {platformInfo.achievements && (
              <div style={{ marginBottom: 24 }}>
                <Title level={4}>主要成就</Title>
                <div style={{ 
                  lineHeight: '1.8', 
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                  padding: '16px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '6px'
                }}>
                  {platformInfo.achievements}
                </div>
              </div>
            )}

            {/* 统计数据预览 */}
            {Object.keys(statisticsMap).length > 0 && (
              <div>
                <Title level={4}>统计数据</Title>
                <Row gutter={[16, 16]}>
                  {Object.entries(statisticsMap).map(([key, value]) => {
                    // 解析组合键：category_displayName
                    const keyParts = key.split('_');
                    let category = '';
                    let displayName = '';
                    
                    if (keyParts.length >= 2) {
                      category = keyParts[0];
                      displayName = keyParts.slice(1).join('_');
                    } else {
                      // 如果没有下划线，直接使用key作为displayName，分类设为other
                      category = 'other';
                      displayName = key;
                    }
                    
                    // 通过数据字典获取中文显示名称
                    const statisticsItem = statisticsItemDict.find(item => item.dictValue === displayName);
                    const chineseDisplayName = statisticsItem?.dictLabel || displayName;
                    
                    // 通过数据字典获取分类的中文显示名称
                    const categoryItem = categoryDict.find(item => item.dictValue === category);
                    const chineseCategory = categoryItem?.dictLabel || category;
                    
                    return (
                      <Col xs={12} sm={8} md={6} key={key}>
                        <div style={{
                          textAlign: 'center',
                          padding: '16px',
                          backgroundColor: '#fafafa',
                          borderRadius: '6px',
                          border: '1px solid #e8e8e8'
                        }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                            {value || 0}
                          </div>
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                            {chineseCategory}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {chineseDisplayName}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 统计数据配置弹框 */}
      <Modal
        title="统计数据配置"
        open={statisticsConfigVisible}
        onCancel={() => setStatisticsConfigVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStatisticsConfigVisible(false)}>
            关闭
          </Button>
        ]}
        width={1200}
        style={{ top: 20 }}
      >
        <StatisticsConfigComponent
          onStatisticsChange={handleStatisticsChange}
          initialStatistics={statisticsMap}
          onStatisticsValuesChange={handleStatisticsValuesChange}
        />
      </Modal>
    </div>
  );
};

export default PlatformManagementNew;